<template>
    <div>
        <!-- 桌面端表格布局 -->
        <div class="hidden md:block overflow-x-auto track-list w-full">
            <table class="table table-pin-rows w-full min-w-[800px]">
                <thead>
                    <tr>
                        <!-- 全选复选框列（批量模式下显示） -->
                        <th v-if="isBatchMode" class="bg-base-200 font-semibold text-base-content/70 w-12">
                            <div class="flex justify-center">
                                <input type="checkbox"
                                       :checked="isAllSelected"
                                       @change="toggleAllSelection"
                                       class="checkbox checkbox-sm" />
                            </div>
                        </th>
                        <!-- # 号列 + 批量选择按钮 -->
                        <th class="bg-base-200 font-semibold text-base-content/70 w-12">
                            <div class="flex items-center justify-center gap-1">
                                <span v-if="!isBatchMode"></span>
                                <button @click="toggleBatchMode"
                                        class="btn btn-xs btn-ghost btn-circle"
                                        :class="{'btn-active': isBatchMode}"
                                        :title="isBatchMode ? $tt('退出批量选择') : $tt('批量选择')">
                                    <svg xmlns="http://www.w3.org/2000/svg" class="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
                                    </svg>
                                </button>
                            </div>
                        </th>
                        <th class="bg-base-200 font-semibold text-base-content/70 min-w-0 max-w-[200px]">{{ $tt('标题') }}</th>
                        <th class="bg-base-200 font-semibold text-base-content/70 min-w-0 max-w-[150px]" v-if="!showImage">{{ $tt('艺术家') }}</th>
                        <th class="bg-base-200 font-semibold text-base-content/70 min-w-0 max-w-[150px]" v-if="!noAlbum">{{ $tt('专辑') }}</th>
                        <th class="bg-base-200 font-semibold text-base-content/70 text-center w-20">{{ $tt('时长') }}</th>
                        <th class="bg-base-200 font-semibold text-base-content/70 text-center w-20">{{ $tt('大小') }}</th>
                        <th class="bg-base-200 font-semibold text-base-content/70 text-center w-20">{{ $tt('比特率') }}</th>
                        <th class="bg-base-200 font-semibold text-base-content/70 text-center w-16">{{ $tt('播放') }}</th>
                        <th class="bg-base-200 font-semibold text-base-content/70 text-center w-20">{{ $tt('操作') }}</th>
                    </tr>
                </thead>
                <tbody>
                    <tr v-for="(item, index) in tracks" :key="`${item.id}_${index}`"
                        :draggable="draggable"
                        @dragstart="dragStart($event, index)"
                        @dragover.prevent
                        @dragenter.prevent
                        @drop="drop($event, index)"
                        @dragend="dragEnd($event)"
                        @mouseenter="handleMouseEnter(item, index, $event)"
                        :class="[
                            'hover:!bg-base-300 cursor-pointer transition-colors duration-200 border-b border-base-200/50',
                            item.id === trackId && isPlaying ? 'bg-primary/10 text-primary' : '',
                            selectedItemIds.includes(getItemId(item, index)) ? 'bg-primary/10' : '',
                            index < 20 ? '' : ''
                        ]"
                        :style="index < 20 ? { 'animation-delay': `${index * 0.05}s` } : {}">
                        <!-- 复选框列（批量模式下显示） -->
                        <td v-if="isBatchMode" class="py-4">
                            <div class="flex justify-center">
                                <div class="checkbox-wrapper"
                                     @mousedown.stop="handleMouseDown(item, index, $event)">
                                    <input type="checkbox"
                                           :checked="selectedItemIds.includes(getItemId(item, index))"
                                           @change.stop="handleCheck(item, index)"
                                           class="checkbox checkbox-sm" />
                                </div>
                            </div>
                        </td>
                        <td @click="play(index)" class="py-4">
                            <div class="flex items-center justify-center">
                                <div v-if="item.id === trackId && isPlaying" class="flex items-center justify-center">
                                    <span class="loading loading-bars loading-sm text-primary"></span>
                                </div>
                                <div v-else class="text-center min-w-[2rem]">
                                    <div v-if="indexField === 'index'" class="text-base-content/70">
                                        {{ index + 1 }}
                                    </div>
                                    <div v-else class="text-base-content/70">
                                        {{ item.track }}
                                    </div>
                                </div>
                            </div>
                        </td>
                        <td @click="play(index)" class="py-4">
                            <div class="flex items-center gap-3">
                                <!-- 专辑封面图片（仅在 showImage 为 true 时显示） -->
                                <div v-if="showImage" class="avatar flex-shrink-0">
                                    <div class="mask mask-squircle w-12 h-12">
                                        <img v-if="item.image" :src="item.image" alt="" />
                                        <img v-else src="/static/dist/img/album_null.jpg" alt="" />
                                    </div>
                                </div>
                                <div class="flex flex-1 min-w-0 flex-col">
                                    <div class="flex items-center gap-2">
                                        <div class="font-medium text-base-content truncate">{{ item.title }}</div>
                                        <div class="badge badge-outline badge-xs" v-if="item.fromFolder === 'webdav'">
                                                <svg t="1705398013860" class="icon fill-current w-3 h-3" viewBox="0 0 1024 1024" version="1.1" xmlns="http://www.w3.org/2000/svg">
                                                    <path d="M533.333333 256a212.416 212.416 0 0 1 212.864 205.333333l0.277334 7.488a52.970667 52.970667 0 0 0 35.413333 48.064l3.136 1.088a131.584 131.584 0 0 1 89.024 109.418667A128.128 128.128 0 0 1 746.666667 768H303.552a152.554667 152.554667 0 0 1-153.301333-132.629333 148.821333 148.821333 0 0 1 123.306666-163.690667l7.296-1.237333a53.034667 53.034667 0 0 0 42.88-40.682667l1.6-7.210667A214.570667 214.570667 0 0 1 533.333333 256m0-64a277.333333 277.333333 0 0 0-270.442666 216.554667A213.226667 213.226667 0 0 0 298.666667 832h448a192 192 0 0 0 63.488-373.034667A277.12 277.12 0 0 0 533.333333 192z"></path>
                                                    <path d="M405.333333 618.666667m32 0l192 0q32 0 32 32l0 0q0 32-32 32l-192 0q-32 0-32-32l0 0q0-32 32-32Z" fill="#FF9C00"></path>
                                                </svg>
                                        </div>
                                    </div>
                                    <!-- 在 showImage 模式下显示艺术家信息 -->
                                    <div v-if="showImage" class="text-sm text-base-content/60 truncate">{{ item.artist }}</div>
                                </div>
                            </div>
                        </td>
                        <td @click="play(index)" class="py-4" v-if="!showImage">
                            <div class="text-base-content/70 truncate">{{ item.artist }}</div>
                        </td>
                        <td @click="play(index)" class="py-4" v-if="!noAlbum">
                            <div class="text-base-content/70 truncate">{{ item.album }}</div>
                        </td>
                        <td @click="play(index)" class="py-4 text-center">
                            <div class="text-base-content/70">{{ formatDuration(item.duration) }}</div>
                        </td>
                        <td @click="play(index)" class="py-4 text-center">
                            <div class="text-base-content/70">{{ formatSize(item.size) }}</div>
                        </td>
                        <td @click="play(index)" class="py-4 text-center">
                            <div class="text-base-content/70">{{ item.bitrate }}kps</div>
                        </td>
                        <td @click="play(index)" class="py-4 text-center">
                            <div class="text-base-content/70">{{ item.playsCount }}</div>
                        </td>
                        <td class="py-4 text-center">
                            <track-action :item="item" :index="index" @open-edit="openTrackEdit"></track-action>
                        </td>
                    </tr>
                </tbody>
            </table>
        </div>

        <!-- 移动端卡片布局 -->
        <div class="md:hidden space-y-2 min-h-[400px]">

            <div v-for="(item, index) in tracks" :key="`${item.id}_${index}`"
                 :draggable="draggable"
                 @dragstart="dragStart($event, index)"
                 @dragover.prevent
                 @dragenter.prevent
                 @drop="drop($event, index)"
                 @dragend="dragEnd($event)"
                 :class="[
                     'card bg-base-100 shadow-sm hover:shadow-md transition-all duration-200 border rounded-lg',
                     item.id === trackId && isPlaying ? 'border-primary shadow-primary/20' : 'border-base-200/30',
                     selectedItemIds.includes(getItemId(item, index)) ? 'border-primary shadow-primary/20' : '',
                     index < 20 ? '' : ''
                 ]"
                 :style="index < 20 ? { 'animation-delay': `${index * 0.05}s` } : {}">
                <div class="card-body p-3"
                     @touchstart="handleTouchStart(item, index)"
                     @touchmove="handleTouchMove"
                     @touchend="handleTouchEnd(index, $event)"
                     @touchcancel="handleTouchCancel">
                    <div class="flex items-center gap-3">
                        <!-- 复选框（批量模式下显示） -->
                        <input v-if="isBatchMode"
                               type="checkbox"
                               :checked="selectedItemIds.includes(getItemId(item, index))"
                               @change.stop="handleCheck(item, index)"
                               class="checkbox checkbox-sm flex-shrink-0" />

                        <!-- 播放状态/序号 -->
                        <div class="flex-shrink-0 w-6 flex items-center justify-center" v-if="!showImage">
                            <div v-if="item.id === trackId && isPlaying" class="flex items-center justify-center">
                                <span class="loading loading-bars loading-xs text-primary"></span>
                            </div>
                            <div v-else class="text-center text-base-content/70 font-medium text-sm">
                                <div v-if="indexField === 'index'">
                                    {{ index + 1 }}
                                </div>
                                <div v-else>
                                    {{ item.track }}
                                </div>
                            </div>
                        </div>

                        <!-- 专辑封面（showImage 模式） -->
                        <div v-if="showImage" class="flex-shrink-0">
                            <div class="avatar">
                                <div class="mask mask-squircle w-12 h-12">
                                    <img v-if="item.image" :src="item.image" alt="" />
                                    <img v-else src="/static/dist/img/album_null.jpg" alt="" />
                                </div>
                            </div>
                        </div>

                        <!-- 歌曲信息 -->
                        <div class="flex-1 min-w-0">
                            <div class="flex items-center gap-2">
                                <h3 class="font-semibold text-sm text-base-content truncate leading-tight">{{ item.title }}</h3>
                                <div class="badge badge-outline badge-xs px-1 py-0.5" v-if="item.fromFolder === 'webdav'">
                                    <svg t="1705398013860" class="icon fill-current w-2.5 h-2.5" viewBox="0 0 1024 1024" version="1.1" xmlns="http://www.w3.org/2000/svg">
                                        <path d="M533.333333 256a212.416 212.416 0 0 1 212.864 205.333333l0.277334 7.488a52.970667 52.970667 0 0 0 35.413333 48.064l3.136 1.088a131.584 131.584 0 0 1 89.024 109.418667A128.128 128.128 0 0 1 746.666667 768H303.552a152.554667 152.554667 0 0 1-153.301333-132.629333 148.821333 148.821333 0 0 1 123.306666-163.690667l7.296-1.237333a53.034667 53.034667 0 0 0 42.88-40.682667l1.6-7.210667A214.570667 214.570667 0 0 1 533.333333 256m0-64a277.333333 277.333333 0 0 0-270.442666 216.554667A213.226667 213.226667 0 0 0 298.666667 832h448a192 192 0 0 0 63.488-373.034667A277.12 277.12 0 0 0 533.333333 192z"></path>
                                        <path d="M405.333333 618.666667m32 0l192 0q32 0 32 32l0 0q0 32-32 32l-192 0q-32 0-32-32l0 0q0-32 32-32Z" fill="#FF9C00"></path>
                                    </svg>
                                </div>
                            </div>
                            <div class="flex items-center gap-1 mt-0.5">
                                <p class="text-xs text-base-content/60 truncate flex-1">{{ item.artist }}</p>
                                <span v-if="!noAlbum && item.album" class="text-xs text-base-content/50">·</span>
                                <p class="text-xs text-base-content/50 truncate max-w-[120px]" v-if="!noAlbum && item.album">{{ item.album }}</p>
                            </div>
                            <div class="flex items-center gap-3 mt-1 text-xs text-base-content/50">
                                <span class="badge badge-ghost badge-xs px-1.5 py-0.5">{{ formatDuration(item.duration) }}</span>
                                <span>{{ formatSize(item.size) }}</span>
                                <span>{{ item.bitrate }}k</span>
                            </div>
                        </div>

                        <!-- 操作按钮 -->
                        <div class="flex-shrink-0" @click.stop @touchstart.stop>
                            <track-action :item="item" :index="index" @open-edit="openTrackEdit"></track-action>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <!-- 空状态 -->
        <div v-if="!tracks || tracks.length === 0" class="flex flex-col items-center justify-center py-16 text-center">
            <h3 class="text-xl font-semibold text-base-content mb-2">{{ $tt('暂无音乐') }}</h3>
            <p class="text-base-content/60">{{ $tt('还没有找到任何音乐，请稍后再试') }}</p>
        </div>

        <!-- 批量操作工具栏（批量模式下显示） -->
        <track-batch-toolbar
            v-if="isBatchMode"
            :selected-items="selectedItems"
            :is-in-playlist="$route.name === 'playlist'"
            :playlist-id="$route.params.id"
            @clear-selection="exitBatchModeAndClearSelection"
            @refresh="$emit('refresh')"
        />

        <dialog ref="trackEditModal" class="modal modal-bottom sm:modal-middle" @close="handleEditModalClose">
            <div class="modal-box !max-w-none !w-screen !h-screen !max-h-screen !rounded-none !p-0 sm:!w-[28rem] sm:!max-w-[28rem] sm:!h-screen sm:!max-h-screen sm:ml-auto sm:mr-0">
                <div class="flex h-full flex-col bg-base-100 text-base-content shadow-xl overflow-y-auto">
                    <div class="sticky top-0 z-10 flex items-center justify-between border-b border-base-200 bg-base-100/95 px-4 py-3 backdrop-blur">
                        <div class="text-xl font-medium">{{ $tt('元数据编辑') }}</div>
                        <button class="btn btn-sm btn-ghost" @click="closeEditModal">
                            <svg class="w-4 h-4 fill-current" viewBox="0 0 24 24">
                                <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z" />
                            </svg>
                        </button>
                    </div>
                    <div class="flex-1 p-4 overflow-y-auto">
                        <music-meta-data
                            v-if="isShowEdit"
                            :loading="editLoading"
                            :reload-img="reloadImg"
                            :music-info="editMusicInfo"
                            :drawer-mode="'drawer'"
                        />
                    </div>
                </div>
            </div>
            <form method="dialog" class="modal-backdrop">
                <button>close</button>
            </form>
        </dialog>

        <dialog ref="resourceModal" class="modal modal-bottom sm:modal-middle" @close="handleResourceModalClose">
            <div class="modal-box !max-w-none !w-screen !h-screen !max-h-screen !rounded-none !p-0 sm:!w-[50rem] sm:!max-w-[50rem] sm:!h-screen sm:!max-h-screen sm:ml-0 sm:mr-auto">
                <div class="flex h-full flex-col bg-base-100 text-base-content shadow-xl overflow-y-auto">
                    <div class="sticky top-0 z-10 flex items-center justify-between border-b border-base-200 bg-base-100/95 px-4 py-3 backdrop-blur">
                        <div class="text-xl font-medium">{{ $tt('资源信息') }}</div>
                        <button class="btn btn-sm btn-ghost" @click="closeResourceModal">
                            <svg class="w-4 h-4 fill-current" viewBox="0 0 24 24">
                                <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z" />
                            </svg>
                        </button>
                    </div>
                    <div class="flex-1 p-4 overflow-y-auto">
                        <resource v-if="isShowResourceId3" :song-list="songList" :section3-loading="resourceLoading"></resource>
                    </div>
                </div>
            </div>
            <form method="dialog" class="modal-backdrop">
                <button>close</button>
            </form>
        </dialog>
    </div>
</template>

<script>
    import TrackAction from "@/views/library/track/TrackAction";
    import TrackBatchToolbar from "@/views/library/track/TrackBatchToolbar";
    import MusicMetaData from '@/views/home/musicMetaData'
    import Resource from '@/views/home/resource'
    import { splitArtistText } from '@/utils/artistSplitter'
    import {mapGetters} from 'vuex'
    export default {
        components: {TrackAction, TrackBatchToolbar, MusicMetaData, Resource},
        props: {
            tracks: {type: Array, required: true},
            noAlbum: {type: Boolean, default: false},
            noArtist: {type: Boolean, default: false},
            noDuration: {type: Boolean, default: false},
            indexField: {type: String, default: 'tracknumber'},
            draggable: {type: Boolean, default: false},
            showImage: {type: Boolean, default: false}, // 是否显示专辑封面图片
            readonly: {type: Boolean, default: false},
        },
        data() {
            return {
                playLists: [],
                id: this.$route.params.id,
                isShowCreatePlist: false,
                selectPlaylist: '',
                selectTrackID: '',
                plName: '',
                draggedIndex: null,
                // 批量选择状态
                isBatchMode: false,       // 批量选择模式开关
                selectedItems: [],        // 选中的完整 track 对象数组
                selectedItemIds: [],      // 唯一标识数组
                // 鼠标拖拽批量选择状态
                isMouseDown: false,
                startItemIndex: null,
                lastHoveredItemIndex: null,
                startItemChecked: false,
                // 移动端长按状态
                longPressTimer: null,
                longPressTriggered: false,
                isShowEdit: false,
                editLoading: false,
                editMusicInfo: {},
                currentEditTrack: null,
                isShowResourceId3: false,
                resourceLoading: false,
                songList: [],
                reloadImg: true,
            }
        },
        computed: {
            ...mapGetters(['trackId']),
            isPlaying() {
                return this.$store.state.common.isPlaying
            },
            // 是否全选
            isAllSelected() {
                return this.tracks.length > 0 && this.selectedItemIds.length === this.tracks.length
            }
        },
        created() {
        },
        mounted() {
            // 添加全局鼠标事件监听器（用于拖拽批量选择）
            document.addEventListener('mouseup', this.handleGlobalMouseUp)
        },
        beforeDestroy() {
            // 移除全局事件监听器
            document.removeEventListener('mouseup', this.handleGlobalMouseUp)

            // 清理长按计时器
            if (this.longPressTimer) {
                clearTimeout(this.longPressTimer)
                this.longPressTimer = null
            }
        },
        watch: {
            // 监听 tracks 变化时重置选中状态
            tracks: {
                handler() {
                    this.selectedItems = []
                    this.selectedItemIds = []
                },
                deep: true
            }
        },
        methods: {
            formatDuration(value) {
                if (!isFinite(value)) {
                    return '∞'
                }
                const minutes = Math.floor(value / 60)
                const seconds = Math.floor(value % 60)
                return (minutes < 10 ? '0' : '') + minutes + ':' + (seconds < 10 ? '0' : '') + seconds
            },
            formatSize(value) {
                return (value / 1024 / 1024).toFixed(1) + ' MB'
            },
            play(index) {
                if (this.readonly) {
                    return
                }
                if (this.tracks[index].id === this.trackId) {
                    return this.$store.dispatch('playPause')
                }
                return this.$store.dispatch('playTrackList', {
                    index,
                    tracks: this.tracks
                })
            },
            dragstart(item, event) {
                if (!item.isStream) {
                    event.dataTransfer.setData('application/x-track-id', item.id)
                }
            },
            addFavouriteTrack(item) {
                if (item.favourite) {
                    this.$api.subsonic.removeFavourite(item.id, 'track').then((res) => {
                        item.favourite = false
                    })
                } else {
                    this.$api.subsonic.addFavourite(item.id, 'track').then((res) => {
                        item.favourite = true
                    })
                }
            },
            removeTrackFromPlaylist(index) {
                this.tracks.splice(index, 1)
            },
            openTrackEdit(item) {
                this.currentEditTrack = item
                this.editLoading = true
                this.isShowEdit = true
                this.isShowResourceId3 = false
                this.songList = []
                if (this.$refs.resourceModal?.open) {
                    this.$refs.resourceModal.close()
                }
                this.$nextTick(() => {
                    if (!this.$refs.trackEditModal?.open) {
                        this.$refs.trackEditModal.showModal()
                    }
                })

                const pathParts = (item.path || '').split('/')
                const fileName = pathParts.pop()
                const parentPath = pathParts.join('/')

                this.$api.Task.musicId3({ file_path: parentPath, file_name: fileName }).then((res) => {
                    this.editLoading = false
                    if (!res.result) {
                        this.$myMsg.notify({
                            title: this.$tt('获取音乐信息失败！'),
                            content: res.message,
                            type: 'error',
                            time: 5000,
                        })
                        this.closeEditModal()
                        return
                    }

                    this.editMusicInfo = {
                        ...res.data,
                        files1: [{
                            name: 'cover.png',
                            status: 'done',
                            url: res.data.artwork
                        }]
                    }
                }).catch(() => {
                    this.editLoading = false
                    this.closeEditModal()
                })
            },
            handleClick() {
                this.closeEditModal()
            },
            toggleLock() {
                if (!this.editMusicInfo.title) {
                    this.$myMsg.notify({
                        title: this.$tt('标题不能为空！'),
                        type: 'error',
                        time: 2000,
                    })
                    return
                }

                this.isShowResourceId3 = true
                this.resourceLoading = true
                this.$nextTick(() => {
                    if (!this.$refs.resourceModal?.open) {
                        this.$refs.resourceModal.showModal()
                    }
                })

                this.$api.Task.fetchId3Title({
                    title: this.editMusicInfo.title,
                    artist: this.editMusicInfo.artist,
                    album: this.editMusicInfo.album,
                    full_path: this.editMusicInfo.path || this.currentEditTrack?.path,
                    resource: localStorage.getItem('resource') || 'netease'
                }).then((res) => {
                    this.resourceLoading = false
                    if (res.result) {
                        this.songList = res.data
                        return
                    }

                    this.$myMsg.notify({
                        title: this.$tt('获取资源信息失败！'),
                        content: res.message,
                        type: 'error',
                        time: 5000,
                    })
                }).catch(() => {
                    this.resourceLoading = false
                    this.$myMsg.notify({
                        title: this.$tt('获取资源信息失败！'),
                        type: 'error',
                        time: 2000,
                    })
                })
            },
            closeEditModal() {
                this.isShowEdit = false
                if (this.$refs.trackEditModal?.open) {
                    this.$refs.trackEditModal.close()
                }
            },
            handleEditModalClose() {
                this.isShowEdit = false
            },
            closeResourceModal() {
                this.isShowResourceId3 = false
                if (this.$refs.resourceModal?.open) {
                    this.$refs.resourceModal.close()
                }
            },
            handleResourceModalClose() {
                this.isShowResourceId3 = false
            },
            handleCopy(k, v, callback) {
                if (k === 'lyric') {
                    const resource = localStorage.getItem('resource') || 'netease'
                    const targetResource = resource !== 'smart_tag' ? resource : v.resource
                    this.$api.Task.fetchLyric({ song_id: v.id, resource: targetResource }).then((res) => {
                        if (res.result && res.data !== '') {
                            this.editMusicInfo.lyrics = res.data
                            this.handleCopy('composer', this.editMusicInfo.lyrics)
                            return
                        }

                        this.$myMsg.notify({
                            title: this.$tt('未找到歌词！'),
                            type: 'error',
                            time: 2000,
                        })
                    }).finally(() => {
                        if (callback && typeof callback === 'function') {
                            callback()
                        }
                    })
                } else if (k === 'composer') {
                    this.$api.Task.extractLyric({ lyc: v }).then((res) => {
                        if (res.result && res.data !== '') {
                            this.editMusicInfo.composer = res.data.composer
                            this.editMusicInfo.lyricist = res.data.lyricist
                        }
                    })
                } else if (k === 'album_img') {
                    this.editMusicInfo.album_img = ''
                    this.editMusicInfo.artwork = v
                    this.editMusicInfo.files1 = [
                        {
                            name: 'new.png',
                            status: 'done',
                            url: v
                        }
                    ]
                    this.reloadImg = false
                    this.$nextTick(() => {
                        this.reloadImg = true
                    })
                } else if (k === 'lyric_tran') {
                    this.editMusicInfo.lyrics = v
                } else if (k === 'artist') {
                    this.editMusicInfo.artist_list = splitArtistText(v, this.$store.state.common.sysDict || {})
                    this.editMusicInfo.artist = v
                } else {
                    this.editMusicInfo[k] = v
                }
            },
            copyAll(item) {
                this.handleCopy('title', item.name)
                this.handleCopy('year', item.year)
                this.handleCopy('lyric', item)
                this.handleCopy('album', item.album)
                this.handleCopy('artist', item.artist)
                this.handleCopy('album_img', item.album_img)
                this.handleCopy('tracknumber', item.tracknumber)
                this.handleCopy('discnumber', item.discnumber)
                this.handleCopy('totaltracks', item.totaltracks)
                this.handleCopy('totaldiscs', item.totaldiscs)
            },
            dragStart(event, index) {
                this.draggedIndex = index;
                event.dataTransfer.effectAllowed = 'move';
                event.target.style.opacity = '0.4';
            },
            dragEnd(event) {
                const rows = event.target.parentElement.getElementsByTagName('tr');
                for (let row of rows) {
                    row.style.opacity = '1';
                }
                // 移动端卡片的处理
                const cards = event.target.parentElement.getElementsByClassName('card');
                for (let card of cards) {
                    card.style.opacity = '1';
                }
            },
            drop(event, index) {
                event.preventDefault();
                const draggedItem = this.tracks[this.draggedIndex];
                const newItem = this.tracks[index];
                this.tracks.splice(this.draggedIndex, 1);
                this.tracks.splice(index, 0, draggedItem);
                
                if (this.$route.name === 'playlist') {
                    this.updatePlaylistOrder(draggedItem.index, newItem.index);
                }
                
                this.draggedIndex = null;
            },
            async updatePlaylistOrder(oldIndex, newIndex) {
                try {
                    const playlistId = this.$route.params.id;

                    await this.$api.subsonic.updatePlaylistOrder(playlistId, oldIndex, newIndex);
                    this.$myMsg.notify({
                        title: this.$tt('更新成功！'),
                        type: 'success',
                        time: 2000,
                    });
                } catch (error) {
                    console.error('Failed to update playlist order:', error);
                    this.$myMsg.notify({
                        title: this.$tt('更新失败！'),
                        type: 'error',
                        time: 2000,
                    });
                }
            },
            // ========== 批量选择相关方法 ==========
            // 生成唯一的 item ID
            getItemId(item, index) {
                return item.id ? `${item.id}-${index}` : `item-${index}`
            },
            // 单项勾选/取消
            handleCheck(item, index) {
                const itemId = this.getItemId(item, index)
                const isSelected = this.selectedItemIds.includes(itemId)

                if (isSelected) {
                    // 取消选中
                    const itemIndex = this.selectedItemIds.indexOf(itemId)
                    if (itemIndex > -1) {
                        this.selectedItemIds.splice(itemIndex, 1)
                        this.selectedItems.splice(itemIndex, 1)
                    }
                } else {
                    // 选中
                    this.selectedItemIds.push(itemId)
                    this.selectedItems.push({ ...item, index })
                }
            },
            // 全选/取消全选
            toggleAllSelection() {
                if (this.isAllSelected) {
                    // 取消全选
                    this.selectedItems = []
                    this.selectedItemIds = []
                } else {
                    // 全选
                    this.selectedItems = this.tracks.map((item, index) => ({ ...item, index }))
                    this.selectedItemIds = this.tracks.map((item, index) => this.getItemId(item, index))
                }
            },
            // 清空选中状态(不改变批量模式状态)
            clearSelection() {
                this.selectedItems = []
                this.selectedItemIds = []
            },
            // 切换批量选择模式
            toggleBatchMode() {
                this.isBatchMode = !this.isBatchMode
                // 退出批量模式时清空选中状态
                if (!this.isBatchMode) {
                    this.clearSelection()
                }
            },
            // 退出批量模式并清空选择(用于工具栏的"取消选择"按钮)
            exitBatchModeAndClearSelection() {
                this.clearSelection()
                this.isBatchMode = false
            },
            // ========== 鼠标拖拽批量选择 ==========
            // 鼠标按下复选框
            handleMouseDown(item, index, event) {
                if (event.button !== 0) return // 只处理左键

                event.preventDefault()
                this.isMouseDown = true
                this.startItemIndex = index
                this.lastHoveredItemIndex = index
                this.startItemChecked = this.selectedItemIds.includes(this.getItemId(item, index))

                // 切换当前项
                this.handleCheck(item, index)
            },
            // 鼠标进入行
            handleMouseEnter(item, index, event) {
                if (!this.isMouseDown || this.startItemIndex === null) return
                if (this.lastHoveredItemIndex === index) return // 防止重复处理

                event.preventDefault()
                this.lastHoveredItemIndex = index

                // 确定选择范围
                const start = Math.min(this.startItemIndex, index)
                const end = Math.max(this.startItemIndex, index)

                // 批量更新范围内的选中状态
                for (let i = start; i <= end; i++) {
                    if (i < this.tracks.length) {
                        const currentItem = this.tracks[i]
                        const currentItemId = this.getItemId(currentItem, i)

                        if (!this.startItemChecked) {
                            // 起始项未选中 -> 选中范围内所有项
                            if (!this.selectedItemIds.includes(currentItemId)) {
                                this.selectedItemIds.push(currentItemId)
                                this.selectedItems.push({ ...currentItem, index: i })
                            }
                        } else {
                            // 起始项已选中 -> 取消选中范围内所有项
                            const itemIndex = this.selectedItemIds.indexOf(currentItemId)
                            if (itemIndex > -1) {
                                this.selectedItemIds.splice(itemIndex, 1)
                                this.selectedItems.splice(itemIndex, 1)
                            }
                        }
                    }
                }
            },
            // 全局鼠标释放
            handleGlobalMouseUp() {
                if (this.isMouseDown) {
                    this.isMouseDown = false
                    this.startItemIndex = null
                    this.lastHoveredItemIndex = null
                    this.startItemChecked = false
                }
            },
            // ========== 移动端长按功能 ==========
            // 触摸开始
            handleTouchStart(item, index) {
                // 如果已经在批量模式下，不触发长按
                if (this.isBatchMode) {
                    return
                }

                this.longPressTriggered = false

                // 开始长按计时器（500ms）
                this.longPressTimer = setTimeout(() => {
                    this.longPressTriggered = true

                    // 触发震动反馈（如果设备支持）
                    if (navigator.vibrate) {
                        navigator.vibrate(50)
                    }

                    // 进入批量模式
                    this.isBatchMode = true

                    // 自动选中当前卡片
                    const itemId = this.getItemId(item, index)
                    if (!this.selectedItemIds.includes(itemId)) {
                        this.selectedItemIds.push(itemId)
                        this.selectedItems.push({ ...item, index })
                    }
                }, 500)
            },
            // 触摸移动
            handleTouchMove() {
                // 如果手指移动，取消长按
                if (this.longPressTimer) {
                    clearTimeout(this.longPressTimer)
                    this.longPressTimer = null
                }
            },
            // 触摸结束
            handleTouchEnd(index, event) {
                // 清除长按计时器
                if (this.longPressTimer) {
                    clearTimeout(this.longPressTimer)
                    this.longPressTimer = null
                }

                // 如果长按已触发，阻止点击事件
                if (this.longPressTriggered) {
                    event.preventDefault()
                    event.stopPropagation()
                    this.longPressTriggered = false
                } else if (!this.isBatchMode) {
                    // 如果没有触发长按且不在批量模式，执行播放
                    this.play(index)
                }
            },
            // 触摸取消
            handleTouchCancel() {
                if (this.longPressTimer) {
                    clearTimeout(this.longPressTimer)
                    this.longPressTimer = null
                }
                this.longPressTriggered = false
            }
        }
    }
</script>

<style scoped>
.track-list {
    min-height: 400px;
    width: 100%;
}

.track-list table {
    table-layout: fixed;
}

/* 复选框包装器 */
.checkbox-wrapper {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    user-select: none;
}

.checkbox-wrapper .checkbox {
    pointer-events: none; /* 防止内部点击事件 */
}

/* 选中行高亮 */
tr.bg-primary\/10 {
    background-color: rgba(var(--p), 0.1) !important;
    transition: background-color 0.2s ease;
}

/* 防止用户选择文本（拖拽时） */
.table tbody tr {
    user-select: none;
}

/* 拖拽样式 */
.cursor-move {
    cursor: move;
}

/* 移动端触摸优化 */
@media (max-width: 768px) {
    .card {
        transition: transform 0.2s ease, box-shadow 0.3s ease;
        -webkit-user-select: none;
        user-select: none;
        -webkit-touch-callout: none;
    }

    .card:active {
        transform: scale(0.98);
    }

    /* 移动端卡片主体区域 */
    .card-body {
        cursor: pointer;
        -webkit-tap-highlight-color: transparent;
    }

    /* 移动端禁用鼠标拖拽 */
    .checkbox-wrapper {
        touch-action: auto;
    }
}

/* 桌面端hover效果 */
@media (min-width: 769px) {
    .track-list tr:hover {
        background-color: hsl(var(--b2));
    }
}

/* 滚动条样式 */
.track-list::-webkit-scrollbar {
    height: 6px;
}

.track-list::-webkit-scrollbar-track {
    background: hsl(var(--b2));
    border-radius: 3px;
}

.track-list::-webkit-scrollbar-thumb {
    background: hsl(var(--bc) / 0.2);
    border-radius: 3px;
}

.track-list::-webkit-scrollbar-thumb:hover {
    background: hsl(var(--bc) / 0.3);
}

/* 播放状态高亮 */
.text-primary {
    color: hsl(var(--p));
}

.bg-primary\/10 {
    background-color: hsl(var(--p) / 0.1);
}

.border-primary {
    border-color: hsl(var(--p));
}

.shadow-primary\/20 {
    box-shadow: 0 10px 15px -3px hsl(var(--p) / 0.2), 0 4px 6px -2px hsl(var(--p) / 0.1);
}

/* 轨道列表动画 */
.track-animate-in {
    opacity: 0;
    animation: trackFadeIn 0.8s ease-out forwards;
}

@keyframes trackFadeIn {
    0% {
        opacity: 0;
        /* transform: translateX(-20px); */
    }
    100% {
        opacity: 1;
        /* 使用 none 结束，避免持久的 stacking context */
        /* transform: none; */
    }
}
</style>
